import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Eye, Loader2 } from 'lucide-react';
import yaml from 'js-yaml';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { Badge } from '../ui/Badge';
import { apiClient } from '../../lib/api-client';
import {
  ResolvedPreviewDialog,
  formatToolSources,
  formatEntityPromptValue,
  formatGlobalPromptValue,
  formatToolResolved,
  pickToolRef,
  pickWorkflowType,
} from './ResolvedPreviewDialog';

export type MergeMode = 'overlay' | 'replace';
export type EntityType = 'stream' | 'product' | 'team' | 'tenant' | 'role';

export interface ToolDefinition {
  id: string;
  label: string;
  description?: string;
  capabilities: Array<{ id: string; label: string; description?: string }>;
}

export interface ToolRef {
  toolId: string;
  enabled: boolean;
  mode: MergeMode;
  capabilities?: string[];
  config?: Record<string, any>;
}

export interface PromptEntry {
  text: string;
  mode: MergeMode;
}

export interface WorkflowTypeConfig {
  id: string;
  label: string;
  executionMode: string;
  stepExecution?: 'independent_agents' | 'same_chat_followups';
  repoType: string;
  reviewGate: string;
  description?: string;
  entityPrompts?: Record<string, PromptEntry>;
  prompts?: Record<string, unknown>;
  tools?: ToolRef[];
}

export interface TaxonomyToolsValue {
  tools: ToolRef[];
  prompts: Record<string, PromptEntry>;
  workflowTypes: WorkflowTypeConfig[];
}

interface TaxonomyToolsEditorProps {
  open: boolean;
  streamId: string;
  entityType: EntityType;
  entityId: string;
  value: TaxonomyToolsValue;
  onChange: (next: TaxonomyToolsValue) => void;
  draftConfigExtras?: Record<string, unknown>;
}

const PROMPT_FIELDS = [
  'classifierSystemPrompt',
  'plannerSystemPrompt',
  'authorSystemPrompt',
  'reviewerSystemPrompt',
  'conversationSystemPrompt',
  'lobbySystemPrompt',
  'executionSystemPrompt',
  'quickWriteSystemPrompt',
  'ingestSystemPrompt',
  'triageFeedbackPrompt',
  'escalationPrompt',
  'contextTriageSystemPrompt',
  'hierarchySystemPrompt',
  'publishSystemPrompt',
  'executionFollowupSystemPrompt',
  'preExecutionSystemPrompt',
  'learnSystemPrompt',
] as const;

function normalizeWorkflowType(raw: Partial<WorkflowTypeConfig> & { id: string }): WorkflowTypeConfig {
  return {
    id: raw.id,
    label: raw.label || raw.id,
    executionMode: raw.executionMode || 'auto',
    stepExecution: raw.stepExecution || 'independent_agents',
    repoType: raw.repoType || '',
    reviewGate: raw.reviewGate || '',
    description: raw.description,
    entityPrompts: raw.entityPrompts,
    prompts: raw.prompts,
    tools: raw.tools,
  };
}

function collectPromptKeysForWorkflowType(
  catalogWt: WorkflowTypeConfig,
  entityWt: WorkflowTypeConfig | undefined,
): string[] {
  const keys = new Set<string>();
  if (catalogWt.entityPrompts) for (const k of Object.keys(catalogWt.entityPrompts)) keys.add(k);
  if (catalogWt.prompts) for (const k of Object.keys(catalogWt.prompts)) keys.add(k);
  if (entityWt?.entityPrompts) for (const k of Object.keys(entityWt.entityPrompts)) keys.add(k);
  for (const f of PROMPT_FIELDS) keys.add(f);
  const order = new Map(PROMPT_FIELDS.map((f, i) => [f, i]));
  return Array.from(keys).sort((a, b) => {
    const oa = order.has(a as (typeof PROMPT_FIELDS)[number]) ? (order.get(a as (typeof PROMPT_FIELDS)[number]) as number) : 1000;
    const ob = order.has(b as (typeof PROMPT_FIELDS)[number]) ? (order.get(b as (typeof PROMPT_FIELDS)[number]) as number) : 1000;
    if (oa !== ob) return oa - ob;
    return a.localeCompare(b);
  });
}

export function TaxonomyToolsEditor({
  open,
  streamId,
  entityType,
  entityId,
  value,
  onChange,
  draftConfigExtras,
}: TaxonomyToolsEditorProps) {
  const [taxonomyCatalog, setTaxonomyCatalog] = useState<WorkflowTypeConfig[]>([]);
  const [toolsCatalog, setToolsCatalog] = useState<ToolDefinition[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);
  const [toolOptions, setToolOptions] = useState<Record<string, any>>({});
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());
  const [expandedWfTypes, setExpandedWfTypes] = useState<Set<string>>(new Set());

  const [previewDlgOpen, setPreviewDlgOpen] = useState(false);
  const [previewDlgTitle, setPreviewDlgTitle] = useState('');
  const [previewDlgLoading, setPreviewDlgLoading] = useState(false);
  const [previewDlgError, setPreviewDlgError] = useState<string | null>(null);
  const [previewOverlayText, setPreviewOverlayText] = useState('');
  const [previewReplaceText, setPreviewReplaceText] = useState<string | undefined>(undefined);

  const tenantCodes = useMemo(() => {
    const fromDraft = Array.isArray(draftConfigExtras?.tenants)
      ? (draftConfigExtras?.tenants as unknown[]).map((code) => String(code || '').trim()).filter(Boolean)
      : [];
    if (fromDraft.length > 0) return fromDraft;
    if (entityType === 'tenant' && entityId) return [entityId];
    return [];
  }, [draftConfigExtras, entityType, entityId]);
  const tenantCodesKey = tenantCodes.join(',');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingTools(true);
    apiClient.getAiConfigTools()
      .then((res) => { if (!cancelled) setToolsCatalog(res.tools || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingTools(false); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || !streamId) return;
    let cancelled = false;
    setCatalogLoading(true);
    apiClient.getAiConfigStreamYaml(streamId)
      .then((res) => {
        if (cancelled) return;
        try {
          const doc = yaml.load(res.yaml || '') as any;
          const root = doc?.stream || doc;
          const wts = root?.taxonomy?.workflowTypes;
          if (Array.isArray(wts)) {
            setTaxonomyCatalog(wts.map((w: any) => normalizeWorkflowType(w)));
          } else {
            setTaxonomyCatalog([]);
          }
        } catch {
          setTaxonomyCatalog([]);
        }
      })
      .catch(() => { if (!cancelled) setTaxonomyCatalog([]); })
      .finally(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; };
  }, [open, streamId]);

  const catalogRows = useMemo(() => {
    const byId = new Map<string, WorkflowTypeConfig>();
    for (const c of taxonomyCatalog) byId.set(c.id, c);
    for (const w of value.workflowTypes) {
      if (!byId.has(w.id)) byId.set(w.id, w);
    }
    return Array.from(byId.values()).sort((a, b) => (a.label || a.id).localeCompare(b.label || b.id));
  }, [taxonomyCatalog, value.workflowTypes]);

  const fetchToolOptions = async (toolId: string) => {
    if (toolId !== 'openshift' && toolOptions[toolId]) return;
    try {
      const res = await apiClient.getAiConfigToolOptions(
        toolId,
        toolId === 'openshift' ? { tenantCodes } : undefined,
      );
      setToolOptions((prev) => ({ ...prev, [toolId]: res.options }));
    } catch {
      // ignore options errors
    }
  };

  useEffect(() => {
    if (!open || !expandedTools.has('openshift')) return;
    void fetchToolOptions('openshift');
  }, [open, tenantCodesKey]);

  const runPreview = async (opts: {
    title: string;
    build: (res: { overlay: any; replace: any }) => { overlay: string; replace?: string };
  }) => {
    setPreviewDlgOpen(true);
    setPreviewDlgTitle(opts.title);
    setPreviewDlgLoading(true);
    setPreviewDlgError(null);
    setPreviewOverlayText('');
    setPreviewReplaceText(undefined);
    try {
      const draftConfig = {
        taxonomy: { workflowTypes: value.workflowTypes },
        tools: value.tools,
        prompts: value.prompts,
        ...(draftConfigExtras || {}),
      };
      const res = await apiClient.previewAiConfigResolve({
        streamId,
        entityType,
        entityId,
        draftConfig,
      });
      const { overlay, replace } = opts.build(res);
      setPreviewOverlayText(overlay);
      if (replace != null && replace !== overlay) {
        setPreviewReplaceText(replace);
      }
    } catch (err: any) {
      setPreviewDlgError(err?.message || 'Preview failed');
    } finally {
      setPreviewDlgLoading(false);
    }
  };

  const update = (patch: Partial<TaxonomyToolsValue>) => onChange({ ...value, ...patch });

  const getToolRef = (toolId: string, refs: ToolRef[] = value.tools): ToolRef | undefined =>
    refs.find((t) => t.toolId === toolId);

  const setToolRefField = (toolId: string, patch: Partial<ToolRef>) => {
    const refs = [...value.tools];
    const idx = refs.findIndex((t) => t.toolId === toolId);
    if (idx >= 0) refs[idx] = { ...refs[idx], ...patch };
    else refs.push({ toolId, enabled: true, mode: 'overlay', ...patch });
    update({ tools: refs });
  };

  const setWorkflowTypeToolsField = (wtId: string, toolId: string, patch: Partial<ToolRef>) => {
    update({
      workflowTypes: value.workflowTypes.map((w) => {
        if (w.id !== wtId) return w;
        const refs = [...(w.tools || [])];
        const idx = refs.findIndex((t) => t.toolId === toolId);
        if (idx >= 0) refs[idx] = { ...refs[idx], ...patch };
        else refs.push({ toolId, enabled: true, mode: 'overlay', ...patch });
        return { ...w, tools: refs };
      }),
    });
  };

  const toggleToolCapability = (toolId: string, capId: string) => {
    const ref = getToolRef(toolId);
    const caps = ref?.capabilities || [];
    const next = caps.includes(capId) ? caps.filter((c) => c !== capId) : [...caps, capId];
    setToolRefField(toolId, { capabilities: next });
  };

  const toggleWfToolCapability = (wtId: string, toolId: string, capId: string) => {
    const wt = value.workflowTypes.find((w) => w.id === wtId);
    const ref = getToolRef(toolId, wt?.tools || []);
    const caps = ref?.capabilities || [];
    const next = caps.includes(capId) ? caps.filter((c) => c !== capId) : [...caps, capId];
    setWorkflowTypeToolsField(wtId, toolId, { capabilities: next });
  };

  const setPromptField = (field: string, text: string) => {
    update({
      prompts: {
        ...value.prompts,
        [field]: { text, mode: value.prompts[field]?.mode || 'overlay' },
      },
    });
  };

  const setPromptMode = (field: string, mode: MergeMode) => {
    update({
      prompts: {
        ...value.prompts,
        [field]: { text: value.prompts[field]?.text || '', mode },
      },
    });
  };

  const toggleWtCatalogRow = (catalogRow: WorkflowTypeConfig, enabled: boolean) => {
    if (enabled) {
      if (value.workflowTypes.some((w) => w.id === catalogRow.id)) return;
      update({
        workflowTypes: [
          ...value.workflowTypes,
          {
            id: catalogRow.id,
            label: catalogRow.label,
            executionMode: catalogRow.executionMode,
            stepExecution: catalogRow.stepExecution || 'independent_agents',
            repoType: catalogRow.repoType,
            reviewGate: catalogRow.reviewGate,
            description: catalogRow.description,
            entityPrompts: {},
            tools: [],
          },
        ],
      });
      return;
    }
    update({ workflowTypes: value.workflowTypes.filter((w) => w.id !== catalogRow.id) });
  };

  const setWtPromptEntry = (wtId: string, field: string, patch: Partial<PromptEntry>) => {
    update({
      workflowTypes: value.workflowTypes.map((w) => {
        if (w.id !== wtId) return w;
        const cur = w.entityPrompts?.[field] || { text: '', mode: 'overlay' as MergeMode };
        return {
          ...w,
          entityPrompts: {
            ...w.entityPrompts,
            [field]: { ...cur, ...patch },
          },
        };
      }),
    });
  };

  return (
    <>
      <Tabs defaultValue="taxonomy">
        <TabsList className="mb-4 w-full">
          <TabsTrigger value="taxonomy">Taxonomy</TabsTrigger>
          <TabsTrigger value="tools">Tools</TabsTrigger>
        </TabsList>

        <TabsContent value="taxonomy">
          <div className="space-y-4">
            {catalogLoading && (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            )}
            {!catalogLoading && catalogRows.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                No workflow types in stream taxonomy. Add types on the stream or enable overrides below when types exist.
              </p>
            )}
            {!catalogLoading && catalogRows.map((row) => {
              const entityWt = value.workflowTypes.find((w) => w.id === row.id);
              const enabled = value.workflowTypes.some((w) => w.id === row.id);
              const expanded = expandedWfTypes.has(row.id);
              const promptKeys = collectPromptKeysForWorkflowType(row, entityWt);
              return (
                <div key={row.id} className="border border-gray-200 dark:border-gray-700 rounded-md">
                  <div className="flex items-center gap-3 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => toggleWtCatalogRow(row, e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600 shrink-0"
                      title="This entity contributes taxonomy overlay for this workflow type"
                    />
                    <button
                      type="button"
                      className="flex-1 flex items-center justify-between gap-2 text-sm text-left min-w-0"
                      onClick={() =>
                        setExpandedWfTypes((prev) => {
                          const next = new Set(prev);
                          if (next.has(row.id)) next.delete(row.id);
                          else next.add(row.id);
                          return next;
                        })
                      }
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                        <span className="font-medium text-gray-900 dark:text-gray-100 truncate">{row.label}</span>
                        <Badge variant="outline" className="text-xs font-mono shrink-0">{row.id}</Badge>
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        <Badge variant="secondary" className="text-xs">{row.executionMode}</Badge>
                        <Badge variant="outline" className="text-[10px]">
                          {row.stepExecution || 'independent_agents'}
                        </Badge>
                      </div>
                    </button>
                  </div>
                  {expanded && enabled && (
                    <div className="px-3 pb-3 space-y-3 border-t border-gray-100 dark:border-gray-800 pt-3">
                      {promptKeys.map((field) => {
                        const entry = entityWt?.entityPrompts?.[field] || { text: '', mode: 'overlay' as MergeMode };
                        return (
                          <div key={field}>
                            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{field}</label>
                              <div className="flex items-center gap-2">
                                <ModeToggle mode={entry.mode} onChange={(m) => setWtPromptEntry(row.id, field, { mode: m })} />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs"
                                  type="button"
                                  onClick={() => void runPreview({
                                    title: `${row.label} · ${field}`,
                                    build: (res) => ({
                                      overlay: formatEntityPromptValue(
                                        pickWorkflowType(res.overlay.taxonomy, row.id) as Record<string, unknown> | null,
                                        field,
                                      ),
                                      replace: formatEntityPromptValue(
                                        pickWorkflowType(res.replace.taxonomy, row.id) as Record<string, unknown> | null,
                                        field,
                                      ),
                                    }),
                                  })}
                                >
                                  <Eye className="w-3 h-3 mr-1" /> Preview
                                </Button>
                              </div>
                            </div>
                            <textarea
                              className="w-full px-2 py-1.5 border rounded text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              rows={3}
                              value={entry.text}
                              onChange={(e) => setWtPromptEntry(row.id, field, { text: e.target.value })}
                              placeholder="Optional - inherits when empty"
                            />
                          </div>
                        );
                      })}

                      <div className="space-y-2 border border-dashed border-gray-200 dark:border-gray-700 rounded-md p-2">
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Tools for this flow</p>
                        {toolsCatalog.map((tool) => {
                          const ref = getToolRef(tool.id, entityWt?.tools || []);
                          const sourceSummary = formatToolSources(tool.id, ref?.config as Record<string, unknown> | undefined);
                          return (
                            <div key={`${row.id}-${tool.id}`} className="rounded border border-gray-200 dark:border-gray-700 p-2">
                              <div className="flex items-center justify-between gap-2">
                                <label className="inline-flex items-center gap-2 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={ref?.enabled ?? false}
                                    onChange={(e) => setWorkflowTypeToolsField(row.id, tool.id, { enabled: e.target.checked })}
                                    className="rounded border-gray-300 dark:border-gray-600"
                                  />
                                  {tool.label}
                                </label>
                                <ModeToggle
                                  mode={ref?.mode || 'overlay'}
                                  onChange={(m) => setWorkflowTypeToolsField(row.id, tool.id, { mode: m })}
                                />
                              </div>
                              {sourceSummary && (
                                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                  {sourceSummary}
                                </p>
                              )}
                              {tool.capabilities.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {tool.capabilities.map((cap) => (
                                    <label key={`${row.id}-${tool.id}-${cap.id}`} className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                      <input
                                        type="checkbox"
                                        checked={ref?.capabilities?.includes(cap.id) ?? false}
                                        onChange={() => toggleWfToolCapability(row.id, tool.id, cap.id)}
                                        className="rounded border-gray-300 dark:border-gray-600"
                                      />
                                      {cap.label}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {expanded && !enabled && (
                    <div className="px-3 pb-3 text-xs text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-2">
                      Enable the workflow type to edit per-type prompts and tools at this entity.
                    </div>
                  )}
                </div>
              );
            })}

            <div className="border border-dashed border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-3">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Global prompts</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Stream-level prompt fields (same payload as before); merged before workflow-type prompts in the engine.
              </p>
              {PROMPT_FIELDS.map((field) => {
                const entry = value.prompts[field];
                return (
                  <div key={field}>
                    <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                      <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{field}</label>
                      <div className="flex items-center gap-2">
                        <ModeToggle mode={entry?.mode || 'overlay'} onChange={(m) => setPromptMode(field, m)} />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          type="button"
                          onClick={() => void runPreview({
                            title: `Global · ${field}`,
                            build: (res) => ({
                              overlay: formatGlobalPromptValue(res.overlay.prompts, field),
                              replace: formatGlobalPromptValue(res.replace.prompts, field),
                            }),
                          })}
                        >
                          <Eye className="w-3 h-3 mr-1" /> Preview
                        </Button>
                      </div>
                    </div>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      value={entry?.text || ''}
                      onChange={(e) => setPromptField(field, e.target.value)}
                      placeholder={`${field} (optional, inherits from parent)`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tools">
          {loadingTools ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {toolsCatalog.map((tool) => {
                const ref = getToolRef(tool.id);
                const isExpanded = expandedTools.has(tool.id);
                const opts = toolOptions[tool.id];
                const sourceSummary = formatToolSources(tool.id, ref?.config as Record<string, unknown> | undefined);
                return (
                  <div key={tool.id} className="border border-gray-200 dark:border-gray-700 rounded-md">
                    <div className="flex items-center gap-3 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={ref?.enabled ?? false}
                        onChange={(e) => setToolRefField(tool.id, { enabled: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                      <button
                        type="button"
                        className="flex-1 flex items-center gap-2 text-sm text-left"
                        onClick={() =>
                          setExpandedTools((prev) => {
                            const next = new Set(prev);
                            if (next.has(tool.id)) next.delete(tool.id);
                            else { next.add(tool.id); void fetchToolOptions(tool.id); }
                            return next;
                          })
                        }
                      >
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        <span className="font-medium text-gray-900 dark:text-gray-100">{tool.label}</span>
                        {tool.description && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{tool.description}</span>
                        )}
                      </button>
                      <ModeToggle mode={ref?.mode || 'overlay'} onChange={(m) => setToolRefField(tool.id, { mode: m })} />
                    </div>
                    {!isExpanded && sourceSummary && (
                      <div className="px-3 pb-2 text-[11px] text-gray-500 dark:text-gray-400">
                        {sourceSummary}
                      </div>
                    )}
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-800 pt-2 space-y-3">
                        {sourceSummary && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{sourceSummary}</p>
                        )}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Capabilities</p>
                          <div className="flex flex-wrap gap-2">
                            {tool.capabilities.map((cap) => (
                              <label key={cap.id} className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                <input
                                  type="checkbox"
                                  checked={ref?.capabilities?.includes(cap.id) ?? false}
                                  onChange={() => toggleToolCapability(tool.id, cap.id)}
                                  className="rounded border-gray-300 dark:border-gray-600"
                                />
                                {cap.label}
                              </label>
                            ))}
                          </div>
                        </div>
                        {opts && Object.keys(opts).length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Configuration</p>
                            {Object.entries(opts).map(([key, values]) => {
                              if (tool.id === 'openshift' && (key === 'adminClusters' || key === 'adminEnabled')) return null;
                              return (
                                <div key={key} className="mb-2">
                                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">{key}</label>
                                  {Array.isArray(values) ? (
                                    <select
                                      multiple
                                      className="w-full border rounded text-xs px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                                      value={ref?.config?.[key] ? (Array.isArray(ref.config[key]) ? ref.config[key] : [ref.config[key]]) : []}
                                      onChange={(e) => {
                                        const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                                        setToolRefField(tool.id, { config: { ...(ref?.config || {}), [key]: selected } });
                                      }}
                                    >
                                      {(values as any[]).map((v: any) => (
                                        <option key={typeof v === 'object' ? v.id || v.displayName : String(v)} value={typeof v === 'object' ? v.id || v.displayName : String(v)}>
                                          {typeof v === 'object' ? v.displayName || v.id || JSON.stringify(v) : String(v)}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <Input
                                      className="text-xs"
                                      value={ref?.config?.[key] || ''}
                                      onChange={(e) => setToolRefField(tool.id, {
                                        config: { ...(ref?.config || {}), [key]: e.target.value },
                                      })}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {tool.id === 'openshift' && opts && (
                          <OpenshiftAdminSection
                            ref={ref}
                            opts={opts}
                            onSetConfig={(patch) => setToolRefField(tool.id, { config: { ...(ref?.config || {}), ...patch } })}
                            onToggleCapability={(capId) => toggleToolCapability(tool.id, capId)}
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={() => void runPreview({
                            title: `Tool: ${tool.label}`,
                            build: (res) => ({
                              overlay: formatToolResolved(pickToolRef(res.overlay.tools, tool.id)),
                              replace: formatToolResolved(pickToolRef(res.replace.tools, tool.id)),
                            }),
                          })}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> Preview Resolved
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ResolvedPreviewDialog
        open={previewDlgOpen}
        onClose={() => {
          setPreviewDlgOpen(false);
          setPreviewDlgError(null);
        }}
        title={previewDlgTitle}
        loading={previewDlgLoading}
        error={previewDlgError}
        overlaySummary={previewOverlayText}
        replaceSummary={previewReplaceText}
      />
    </>
  );
}

function ModeToggle({ mode, onChange }: { mode: MergeMode; onChange: (m: MergeMode) => void }) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 text-xs">
      <button
        type="button"
        className={`px-2 py-0.5 rounded-l-md transition-colors ${
          mode === 'overlay'
            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium'
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
        onClick={() => onChange('overlay')}
      >
        Overlay
      </button>
      <button
        type="button"
        className={`px-2 py-0.5 rounded-r-md transition-colors ${
          mode === 'replace'
            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-medium'
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
        onClick={() => onChange('replace')}
      >
        Replace
      </button>
    </div>
  );
}

interface AdminClusterOption {
  id: string;
  displayName: string;
  classification?: string;
  reason?: string;
}

function OpenshiftAdminSection({
  ref,
  opts,
  onSetConfig,
  onToggleCapability,
}: {
  ref: ToolRef | undefined;
  opts: { adminClusters?: AdminClusterOption[]; adminEnabled?: boolean };
  onSetConfig: (patch: Record<string, unknown>) => void;
  onToggleCapability: (capId: string) => void;
}) {
  const adminEnabled = opts.adminEnabled === true;
  const adminClusters = opts.adminClusters || [];
  const selectedAdminClusters: string[] = Array.isArray(ref?.config?.adminClusters)
    ? ref!.config!.adminClusters
    : [];
  const adminConfirmed = Boolean(ref?.config?.adminConfirmed);
  const hasAdminCap = Boolean(ref?.capabilities?.includes('admin_nonprod'));

  if (!adminEnabled) {
    return (
      <p className="text-xs text-amber-600 dark:text-amber-400">
        Admin mode is disabled globally (AI_OPENSHIFT_ADMIN_ENABLED is not true). Enabling the
        capability here has no effect until the global switch is on.
      </p>
    );
  }

  return (
    <div className="border border-amber-300 dark:border-amber-700 rounded-md p-2 space-y-2 bg-amber-50 dark:bg-amber-950/30">
      <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
        OpenShift Admin Mode (non-production only)
      </p>
      <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={hasAdminCap}
          onChange={() => onToggleCapability('admin_nonprod')}
          className="rounded border-gray-300 dark:border-gray-600"
        />
        Enable admin_nonprod capability
      </label>
      {hasAdminCap && (
        <>
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Warning: this grants unrestricted, terminating `oc` commands (including pod shell via
            non-interactive `oc exec`) on selected non-production clusters. Production and unknown
            clusters are always denied. You must confirm below.
          </p>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
              Admin clusters (non-production only)
            </label>
            <select
              multiple
              className="w-full border rounded text-xs px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
              value={selectedAdminClusters}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                onSetConfig({ adminClusters: selected });
              }}
            >
              {adminClusters.length === 0 ? (
                <option disabled>No non-production clusters available</option>
              ) : (
                adminClusters.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName} ({c.classification})
                  </option>
                ))
              )}
            </select>
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={adminConfirmed}
              onChange={(e) => {
                if (e.target.checked) {
                  const confirmText = window.confirm(
                    'Confirm: I understand this grants unrestricted oc commands on the selected non-production clusters.',
                  );
                  onSetConfig({ adminConfirmed: confirmText });
                } else {
                  onSetConfig({ adminConfirmed: false, adminClusters: [] });
                }
              }}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            I confirm admin access and accept the risk
          </label>
        </>
      )}
    </div>
  );
}
